export interface VoucherFields {
  transactionRef?: string;
  depositorName?: string;
  destAccount?: string;
  amount?: string;
  date?: string;
  bank?: string;
}

/**
 * Reads a deposit voucher with Tesseract and pulls out the fields we store.
 *
 * Runs entirely in the browser: no API key, no rate limit, no vendor
 * availability to depend on. The trade-off is that Tesseract only reads printed
 * text well — handwritten vouchers come back mostly empty and the user types
 * those in.
 *
 * The parsing is line-based rather than regex-over-the-whole-blob because real
 * vouchers put the label on one line and the value on the next ("Nro. de
 * transacción" ⏎ "348046624") at least as often as they put both on one line.
 * Every rule here was derived from actual receipts (Económico, BNB, BISA, Fie,
 * Ganadero, Mercantil, BancoSol, Yasta, Yolopago) — see the test file, which
 * holds the OCR output of each. When a bank's layout is missed, add its wording
 * to the label list and add its text to the tests.
 */

const MONTHS: Record<string, string> = {
  enero: '01', ene: '01',
  febrero: '02', feb: '02',
  marzo: '03', mar: '03',
  abril: '04', abr: '04',
  mayo: '05', may: '05',
  junio: '06', jun: '06',
  julio: '07', jul: '07',
  agosto: '08', ago: '08',
  septiembre: '09', setiembre: '09', sep: '09', sept: '09',
  octubre: '10', oct: '10',
  noviembre: '11', nov: '11',
  diciembre: '12', dic: '12',
};

/** Longest first, so "Banco de Crédito de Bolivia" wins over "Banco de Crédito". */
const BANKS = [
  'Banco de Crédito de Bolivia', 'Banco Nacional de Bolivia', 'Banco Mercantil Santa Cruz',
  'Banco Solidario', 'Banco Económico', 'Banco Ganadero', 'Banco de Crédito',
  'Banco Unión', 'Banco Fassil', 'Banco Bisa', 'Banco Fie', 'BancoFie', 'BancoSol',
  'Banco Mercantil', 'Prodem', 'BISA', 'BNB', 'BCP',
].sort((a, b) => b.length - a.length);

/** Words that mean a line is a label, not a person's name. */
const NAME_STOPWORDS =
  /banco|cuenta|origen|destino|nombre|titular|importe|monto|total|fecha|hora|transacci|comprobante|referencia|c[oó]digo|nit|carnet|tarjeta|suma|pago|dep[oó]sito|nro|n[uú]mero|detalle|servicio|concepto|sucursal|oficina|canal|criterio|medidor|factura|bancarizaci|cotizaci|solicitante|remitente|originante|destinatario|electr[oó]nico|operaci|autorizaci|tel[eé]fono|enviado|acredit|debit/i;

const DATE_LIKE = /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/;

const strip = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function toLines(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean);
}

/**
 * Finds a value by label. Looks on the label's own line first; only falls
 * through to following lines when the label line held nothing but the label —
 * otherwise a label whose value failed validation (BNB's "Referencia: Taxi")
 * would swallow the next line's date.
 */
function findByLabel(
  lines: string[],
  label: RegExp,
  extract: (s: string) => string | undefined,
  lookahead = 2,
): string | undefined {
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(label);
    if (!m) continue;

    const rest = lines[i].slice((m.index ?? 0) + m[0].length);
    const onLine = extract(rest);
    if (onLine) return onLine;

    // Look below only when the label line carried no words of its own. A label
    // followed by an account number ("Cuenta de origen 1003757796" ⏎ the name)
    // still needs the next line, but one followed by a word that simply failed
    // validation ("Referencia: Taxi") must not swallow the line after it.
    if (/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(rest)) continue;

    for (let j = 1; j <= lookahead && i + j < lines.length; j++) {
      const below = extract(lines[i + j]);
      if (below) return below;
    }
  }
  return undefined;
}

/** Tries each label set in priority order. */
function findFirst(
  lines: string[],
  labels: RegExp[],
  extract: (s: string) => string | undefined,
  lookahead = 2,
): string | undefined {
  for (const label of labels) {
    const found = findByLabel(lines, label, extract, lookahead);
    if (found) return found;
  }
  return undefined;
}

// ── Value extractors ────────────────────────────────────────────────────────

/** A reference must contain a digit, so "Referencia: Taxi" is rejected. */
function extractRef(s: string): string | undefined {
  const tokens = s.match(/[A-Za-z0-9][A-Za-z0-9/-]{4,}/g) ?? [];
  return tokens.find((t) => /\d/.test(t) && !DATE_LIKE.test(t));
}

/** Longest digit run, keeping `*` so masked accounts (250****509) survive. */
function extractAccount(s: string): string | undefined {
  const matches = s.match(/\d[\d*\s-]{3,}[\d*]/g) ?? [];
  const best = matches
    .map((m) => m.replace(/[\s-]/g, ''))
    .filter((m) => (m.match(/[\d*]/g) ?? []).length >= 5)
    .sort((a, b) => b.length - a.length)[0];
  return best;
}

function extractName(raw: string): string | undefined {
  for (const segment of raw.split(/[|:]/).map((s) => s.trim()).filter(Boolean)) {
    const cleaned = segment
      .replace(/\([^)]*\)/g, ' ')
      .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned || NAME_STOPWORDS.test(cleaned)) continue;

    const words = cleaned.split(' ').filter((w) => w.replace(/\./g, '').length >= 2);
    // Drop a trailing initialism like the "CA" in "... VICTOR BYRON CA: 3071974406".
    if (words.length > 2 && words[words.length - 1].length <= 2) words.pop();
    if (words.length >= 2 && cleaned.length >= 5 && cleaned.length <= 60) {
      return words.join(' ');
    }
  }
  return undefined;
}

/**
 * Vouchers disagree on separators: `Bs15.000,00` uses comma decimals while
 * `Bs 4,200.00` uses dot decimals, so the last separator decides.
 */
export function normalizeAmount(raw: string): string | undefined {
  let c = raw.replace(/[^\d.,]/g, '');
  if (!c) return undefined;

  // Grouped with no decimal part ("15.000", "4,200") is thousands, not a decimal.
  if (/^\d{1,3}([.,]\d{3})+$/.test(c)) {
    c = c.replace(/[.,]/g, '');
  } else {
    const lastComma = c.lastIndexOf(',');
    const lastDot = c.lastIndexOf('.');
    if (lastComma > lastDot) c = c.replace(/\./g, '').replace(',', '.');
    else if (lastDot > lastComma) c = c.replace(/,/g, '');
  }

  const n = parseFloat(c);
  return Number.isFinite(n) && n > 0 ? String(n) : undefined;
}

function extractAmount(s: string): string | undefined {
  const cleaned = s.replace(/bs\.?/gi, ' ');
  // OCR sometimes drops the decimal separator: "MONTO BS 3.800 50".
  const spaced = cleaned.match(/(\d{1,3}(?:[.,]\d{3})+)\s+(\d{2})(?!\d)/);
  if (spaced) return normalizeAmount(`${spaced[1]},${spaced[2]}`);

  const m = cleaned.match(/\d[\d.,]*/);
  return m ? normalizeAmount(m[0]) : undefined;
}

function buildDate(day: string, month: string, year: string): string | undefined {
  const y = year.length === 2 ? `20${year}` : year;
  const d = day.padStart(2, '0');
  const mo = month.padStart(2, '0');
  if (+mo < 1 || +mo > 12 || +d < 1 || +d > 31 || y.length !== 4) return undefined;
  return `${y}-${mo}-${d}`;
}

/** Handles `24/03/2026`, `17/Jul/2026` and `20 de octubre, 2025`. */
export function extractDate(s: string): string | undefined {
  const numeric = s.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (numeric) return buildDate(numeric[1], numeric[2], numeric[3]);

  const named = s.match(/(\d{1,2})[/\s-]+(?:de\s+)?([A-Za-zÁÉÍÓÚáéíóú]{3,})[/\s,-]+(?:de\s+)?(\d{4})/);
  if (named) {
    const month = MONTHS[strip(named[2])];
    if (month) return buildDate(named[1], month, named[3]);
  }
  return undefined;
}

function findBank(lines: string[]): string | undefined {
  const match = (text: string) => {
    const n = strip(text);
    return BANKS.find((b) => n.includes(strip(b)));
  };

  // Prefer the destination bank — that is the account the money landed in.
  for (let i = 0; i < lines.length; i++) {
    if (!/destino|acredit[oó]/i.test(lines[i])) continue;
    const found = match(lines.slice(i, i + 3).join(' '));
    if (found) return found;
  }
  return match(lines.join(' '));
}

// ── Label sets, in priority order ───────────────────────────────────────────

const REF_LABELS = [
  /(?:nro\.?|n[uú]mero|no\.?)\s*(?:de\s*)?(?:transacci[oó]n|[oó]rden|operaci[oó]n|comprobante)/i,
  /c[oó]digo\s*de\s*transacci[oó]n/i,
  /transacci[oó]n\s*n[°º*.]?/i,
  /referencia/i,
  /bancarizaci[oó]n/i,
  /c[oó]digo\s*de\s*autorizaci[oó]n/i,
];

const ACCOUNT_LABELS = [
  /cuenta\s*(?:de\s*)?destino/i,
  /se\s*acredit[oó]\s*a\s*la\s*cuenta/i,
  /cuenta\s*abonada/i,
];

const NAME_LABELS = [
  /remitente|originante|ordenante/i,
  /cuenta\s*(?:de\s*)?origen/i,
  /solicitante|titular/i,
];

const AMOUNT_LABELS = [
  /importe(?:\s*a\s*pagar)?/i,
  /monto/i,
  /la\s*suma\s*de/i,
  /transferidos?|transteridos?/i,
  /total/i,
  /bs\.?/i,
];

const DATE_LABELS = [
  /fecha\s*(?:y\s*hora\s*)?(?:de\s*(?:la\s*)?(?:transacci[oó]n|transferencia|pago))?/i,
];

export function parseVoucherText(text: string): VoucherFields {
  const lines = toLines(text);
  const fields: VoucherFields = {};

  const ref = findFirst(lines, REF_LABELS, extractRef);
  if (ref) fields.transactionRef = ref;

  const account = findFirst(lines, ACCOUNT_LABELS, extractAccount);
  if (account) fields.destAccount = account;

  const name = findFirst(lines, NAME_LABELS, extractName);
  if (name) fields.depositorName = name;

  const amount = findFirst(lines, AMOUNT_LABELS, extractAmount);
  if (amount) fields.amount = amount;

  // Fall back to any date in the document — labels vary too much to rely on.
  const date = findFirst(lines, DATE_LABELS, extractDate) ?? extractDate(text);
  if (date) fields.date = date;

  const bank = findBank(lines);
  if (bank) fields.bank = bank;

  return fields;
}

export async function extractVoucherFields(
  image: string | File | Blob,
  onProgress?: (percent: number) => void,
): Promise<{ fields: VoucherFields; rawText: string }> {
  // Loaded on demand so the OCR engine stays out of the main bundle — it is only
  // needed when someone actually clicks to read a voucher.
  const { createWorker } = await import('tesseract.js');

  const worker = await createWorker('spa', undefined, {
    logger: onProgress
      ? (m: { status: string; progress: number }) => {
          if (m.status === 'recognizing text') onProgress(Math.round(m.progress * 100));
        }
      : undefined,
  });

  try {
    const { data } = await worker.recognize(image);
    return { fields: parseVoucherText(data.text), rawText: data.text };
  } finally {
    await worker.terminate();
  }
}
